import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { mentionIconText } from '../utils/mentionUtils';

/**
 * Small mark on a source block that is embedded elsewhere as a snippet.
 * Click → list of consumer docs (optional open).
 */
export const SnippetBacklinkBadge = ({
  links = [],
  resolveDocMeta,
  onOpenDoc,
}) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const count = links.length;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
      }
    };
    const onClick = (e) => {
      if (
        menuRef.current?.contains(e.target) ||
        btnRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  if (!count) return null;

  const label = count === 1 ? 'Referenced in 1 doc' : `Referenced in ${count} docs`;

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setMenuPos({ x: r.right - 200, y: r.bottom + 4 });
    }
    setOpen((v) => !v);
  };

  // Unique consumer docs (same doc can embed a block more than once)
  const consumers = [];
  const seen = new Set();
  for (const link of links) {
    if (seen.has(link.consumerDocId)) continue;
    seen.add(link.consumerDocId);
    const meta = resolveDocMeta?.(link.consumerDocId);
    consumers.push({
      id: link.consumerDocId,
      title: meta?.title || 'Untitled Doc',
      icon: meta?.icon,
    });
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="snippet-backlink-badge"
        contentEditable={false}
        title={label}
        aria-label={label}
        onClick={toggle}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <span className="snippet-backlink-badge__icon" aria-hidden="true">
          ⧉
        </span>
        <span className="snippet-backlink-badge__count">{count}</span>
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="snippet-backlink-menu"
            style={{ top: menuPos.y, left: Math.max(8, menuPos.x) }}
            contentEditable={false}
          >
            <div className="snippet-backlink-menu__title">{label}</div>
            {consumers.map((doc) => (
              <button
                key={doc.id}
                type="button"
                className="snippet-backlink-menu__item"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onOpenDoc?.(doc.id);
                }}
              >
                <span className="snippet-backlink-menu__icon">
                  {mentionIconText(doc.icon)}
                </span>
                <span className="snippet-backlink-menu__label">{doc.title}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};
